import React, { FC, useMemo } from 'react';
import { CoverItem, Platform, DeviceType, Orientation, WriteDirection } from 'sofa-reborn-react';
import Icon from '@ingka/ssr-icon';
import PlusIconPaths from '@ingka/ssr-icon/paths/plus';
import ChevronRightSmallIconPaths from '@ingka/ssr-icon/paths/chevron-right-small';
import ChevronLeftSmallIconPaths from '@ingka/ssr-icon/paths/chevron-left-small';
import { KompisText } from '@inter-ikea-kompis/react-components';
import {
	StyledCoverPickerButton,
	SelectedCoverWrapper,
	SelectedCover,
	Covers,
	Cover,
	PlusIconWrapper,
	IconWrapper,
	CoverContainer,
	CoverIconContainer,
	IconContainer,
} from './CoverPickerButtonStyle';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { localeService } from '../../services';
import { StringUtility } from '@inter-ikea-kompis/utilities';

type Props = {
	covers: CoverItem[];
	addPlusSign: boolean;
	selectedCover: CoverItem;
	platform: Platform;
	deviceType: DeviceType;
	orientation: Orientation;
	writeDirection: WriteDirection;
	hasArmrestPicker: boolean;
	totalNrOfCovers: number;
	isHovering: boolean;
	languageCode: string;
	onButtonClick?: () => void;
	onPointerOver?: () => void;
	onPointerLeave?: () => void;
};

const CoverPickerButton: FC<Props> = (props) => {
	const { onButtonClick, onPointerOver, onPointerLeave } = props;

	const selectedCoverName = useMemo(() => {
		const currentCover = props.covers.find(
			(cover) => cover.coverName === props.selectedCover.coverName
		);
		return currentCover ? currentCover.name : '';
	}, [props.selectedCover, props.languageCode]);

	const colorElements = props.covers.map(function (cover: CoverItem, key: number) {
		return (
			<Cover
				key={key}
				color={cover.color}
				borderColor={cover.borderColor as string}
				orientation={props.orientation}
				deviceType={props.deviceType}
				platform={props.platform}
				writeDirection={props.writeDirection}
				selected={cover.coverName === props.selectedCover.coverName}
				coverId={key}
			></Cover>
		);
	});
	if (props.addPlusSign) {
		colorElements.push(
			<PlusIconWrapper
				key={props.covers.length + 1}
				orientation={props.orientation}
				deviceType={props.deviceType}
				platform={props.platform}
				writeDirection={props.writeDirection}
				coverId={1}
			>
				<IconWrapper>
					{props.deviceType === 'mobile' || props.deviceType === 'tablet' ? (
						<Icon paths={PlusIconPaths}></Icon>
					) : (
						<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.headingXSmall}>
							{'+' + (props.totalNrOfCovers - 6)}
						</KompisText>
					)}
				</IconWrapper>
			</PlusIconWrapper>
		);
	}

	return (
		<StyledCoverPickerButton
			onClick={(): void => onButtonClick && onButtonClick()}
			onKeyDown={(e): void => {
				if (e.key === 'Enter' || e.key === ' ') {
					onButtonClick && onButtonClick();
				}
			}}
			onPointerOver={(): void => onPointerOver && onPointerOver()}
			onPointerLeave={(): void => onPointerLeave && onPointerLeave()}
			platform={props.platform}
			orientation={props.orientation}
			deviceType={props.deviceType}
			writeDirection={props.writeDirection}
			hasArmrestPicker={props.hasArmrestPicker}
			role="button"
			aria-label={localeService.getTranslation('coverPickerButtonLabel')}
			tabIndex={0}
			id={'coverPickerButton'}
		>
			{props.deviceType !== 'tablet' && props.deviceType !== 'mobile' ? (
				<CoverIconContainer isKiosk={props.platform === 'kiosk'}>
					<CoverContainer>
						<KompisText
							theme={SkapaTheme}
							type={ThemeFontStyleTypeEnum.headingXSmall}
							underline={props.isHovering}
						>
							{localeService.getTranslation('selectCover')}
						</KompisText>
						<KompisText theme={SkapaTheme}>
							{StringUtility.capitalizeFirstLetter(selectedCoverName as string)}
						</KompisText>
					</CoverContainer>
					<IconContainer>
						<Icon
							paths={
								props.writeDirection === 'ltr'
									? ChevronRightSmallIconPaths
									: ChevronLeftSmallIconPaths
							}
						></Icon>
					</IconContainer>
				</CoverIconContainer>
			) : null}
			{(props.deviceType === 'mobile' || props.deviceType === 'tablet') && (
				<SelectedCoverWrapper
					isTablet={props.deviceType === 'tablet'}
					isMobile={props.deviceType === 'mobile'}
				>
					<SelectedCover
						color={props.selectedCover.color}
						deviceType={props.deviceType}
						platform={props.platform}
						orientation={props.orientation}
						writeDirection={props.writeDirection}
						borderColor={props.selectedCover.borderColor as string}
					></SelectedCover>
				</SelectedCoverWrapper>
			)}
			{(props.deviceType === 'mobile' || props.deviceType === 'tablet') &&
			props.hasArmrestPicker ? null : (
				<Covers
					deviceType={props.deviceType}
					platform={props.platform}
					orientation={props.orientation}
					writeDirection={props.writeDirection}
				>
					{colorElements}
				</Covers>
			)}
		</StyledCoverPickerButton>
	);
};
export default CoverPickerButton;
